import './ui/tokens.css';
import { useEffect, useState } from 'react';

const fontStack = "'Plus Jakarta Sans', 'Segoe UI', 'Arial', sans-serif";

export const THEME_MODE_DARK = 'dark';
export const THEME_MODE_LIGHT = 'light';
export const DEFAULT_ACCENT_1 = '#3b82f6';
export const DEFAULT_ACCENT_2 = '#6366f1';
export const DEFAULT_WHITE_MODE_ACCENT = '#2563eb';

function normalizeThemeMode(value) {
  return value === THEME_MODE_LIGHT ? THEME_MODE_LIGHT : THEME_MODE_DARK;
}

export function getBoardThemeMode() {
  if (typeof document === 'undefined') return THEME_MODE_DARK;
  return normalizeThemeMode(document.documentElement?.dataset?.boardTheme);
}

export function useBoardThemeMode() {
  const [themeMode, setThemeMode] = useState(() => getBoardThemeMode());

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const root = document.documentElement;
    if (!root) return undefined;

    const syncThemeMode = () => setThemeMode(getBoardThemeMode());
    syncThemeMode();

    const observer = new MutationObserver((mutations) => {
      if (mutations.some((mutation) => mutation.attributeName === 'data-board-theme')) {
        syncThemeMode();
      }
    });

    observer.observe(root, { attributes: true, attributeFilter: ['data-board-theme'] });
    return () => observer.disconnect();
  }, []);

  return themeMode;
}

function hexToRgba(hex, alpha) {
  const normalized = String(hex || '').replace('#', '');
  if (!/^[a-f0-9]{6}$/i.test(normalized)) return `rgba(59,130,246,${alpha})`;
  const value = Number.parseInt(normalized, 16);
  const red = (value >> 16) & 255;
  const green = (value >> 8) & 255;
  const blue = value & 255;
  return `rgba(${red},${green},${blue},${alpha})`;
}

function parseHex(hex) {
  const normalized = String(hex || '').replace('#', '');
  if (!/^[a-f0-9]{6}$/i.test(normalized)) return null;
  const value = Number.parseInt(normalized, 16);
  return {
    r: (value >> 16) & 255,
    g: (value >> 8) & 255,
    b: value & 255,
  };
}

function toHex(value) {
  return Math.max(0, Math.min(255, Math.round(value))).toString(16).padStart(2, '0');
}

function resolveHex(value, fallback) {
  return parseHex(value) ? value : fallback;
}

function mixColors(colorA, colorB, ratio = 0.5) {
  const a = parseHex(colorA) || parseHex(DEFAULT_ACCENT_1);
  const b = parseHex(colorB) || parseHex(DEFAULT_ACCENT_2);
  const mix = {
    r: a.r + (b.r - a.r) * ratio,
    g: a.g + (b.g - a.g) * ratio,
    b: a.b + (b.b - a.b) * ratio,
  };
  return `#${toHex(mix.r)}${toHex(mix.g)}${toHex(mix.b)}`;
}

function shadeColor(hex, amount = 0) {
  const color = parseHex(hex) || parseHex(DEFAULT_ACCENT_1);
  return `#${toHex(color.r + amount)}${toHex(color.g + amount)}${toHex(color.b + amount)}`;
}



function getRelativeLuminance(hex) {
  const color = parseHex(hex) || parseHex(DEFAULT_ACCENT_1);
  const channels = [color.r, color.g, color.b].map((value) => {
    const normalized = value / 255;
    return normalized <= 0.03928 ? normalized / 12.92 : ((normalized + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
}

function resolveLightAccent(accent) {
  const base = resolveHex(accent, DEFAULT_WHITE_MODE_ACCENT);
  return {
    base,
    strong: shadeColor(base, -12),
    soft: mixColors(base, '#ffffff', 0.16),
  };
}

export function getReadableTextColor(hex) {
  return getRelativeLuminance(hex) > 0.58 ? '#0f172a' : '#ffffff';
}

export function brandedPageStyle(mode = THEME_MODE_DARK) {
  return {...theme.page,background:mode===THEME_MODE_LIGHT?'#f5f8fc':'#0c1622'};
}

export function primaryButtonStyle(accent1 = DEFAULT_ACCENT_1, accent2 = DEFAULT_ACCENT_2, options = {}) {
  const mode = normalizeThemeMode(options.mode);
  const whiteAccent = resolveHex(options.whiteAccent, DEFAULT_WHITE_MODE_ACCENT);

  if (mode === THEME_MODE_LIGHT) {
    const accent = resolveLightAccent(whiteAccent);
    return {
      ...theme.primaryButton,
      background: `linear-gradient(135deg, ${accent.base} 0%, ${accent.soft} 100%)`,
      color: getReadableTextColor(accent.base),
      border: `1px solid ${hexToRgba(accent.base, 0.26)}`,
    };
  }

  const first = resolveHex(accent1, DEFAULT_ACCENT_1);
  const second = resolveHex(accent2, DEFAULT_ACCENT_2);
  const mixed = mixColors(first, second, 0.5);
  return {
    ...theme.primaryButton,
    background: `linear-gradient(135deg, ${first} 0%, ${second} 100%)`,
    color: getReadableTextColor(mixed),
    border: `1px solid ${hexToRgba(mixed, 0.42)}`,
  };
}

export function applyThemeVariables(
  mode = THEME_MODE_DARK,
  accent1 = DEFAULT_ACCENT_1,
  accent2 = DEFAULT_ACCENT_2,
  whiteAccent = DEFAULT_WHITE_MODE_ACCENT,
) {
  const root = document.documentElement;
  if (!root) return;

  const normalizedMode = normalizeThemeMode(mode);
  root.dataset.boardTheme = normalizedMode;
  root.style.colorScheme = normalizedMode;
  root.style.setProperty('--board-color-scheme', normalizedMode);

  if (normalizedMode === THEME_MODE_LIGHT) {
    const accent = resolveLightAccent(whiteAccent);
    const mixed = mixColors(accent.base, accent.soft, 0.22);
    const buttonText = getReadableTextColor(accent.base);

    root.style.setProperty('--brand-accent-1', accent.base);
    root.style.setProperty('--brand-accent-2', accent.soft);
    root.style.setProperty('--brand-mix', mixed);
    root.style.setProperty('--board-accent-soft', hexToRgba(accent.base, 0.10));
    root.style.setProperty('--board-accent-border', hexToRgba(accent.base, 0.22));
    root.style.setProperty('--board-accent-gradient-soft', `linear-gradient(135deg, ${hexToRgba(accent.base, 0.16)} 0%, rgba(255,255,255,0.95) 100%)`);
    root.style.setProperty('--brand-button-bg', `linear-gradient(135deg, ${accent.base} 0%, ${accent.soft} 100%)`);
    root.style.setProperty('--brand-button-text', buttonText);
    root.style.setProperty('--brand-button-border', hexToRgba(accent.base, 0.24));
    root.style.setProperty('--brand-secondary-bg', 'rgba(15,23,42,0.04)');
    root.style.setProperty('--brand-secondary-text', '#1f2937');
    root.style.setProperty('--brand-secondary-border', 'rgba(148,163,184,0.22)');

    root.style.setProperty('--board-page-text', '#111827');
    root.style.setProperty('--board-text-strong', '#0f172a');
    root.style.setProperty('--board-text-soft', '#334155');
    root.style.setProperty('--board-text-muted', '#64748b');
    root.style.setProperty('--board-text-faint', '#94a3b8');
    root.style.setProperty('--board-surface', '#ffffff');
    root.style.setProperty('--board-surface-strong', '#ffffff');
    root.style.setProperty('--board-surface-soft', '#f6f8fb');
    root.style.setProperty('--board-surface-soft-strong', '#eef2f7');
    root.style.setProperty('--board-border', 'rgba(148,163,184,0.20)');
    root.style.setProperty('--board-border-strong', 'rgba(148,163,184,0.30)');
    root.style.setProperty('--board-planner-grid-line', 'rgba(71,85,105,0.18)');
    root.style.setProperty('--board-planner-grid-line-strong', 'rgba(71,85,105,0.28)');
    root.style.setProperty('--board-planner-track-sheen', 'rgba(15,23,42,0.04)');
    root.style.setProperty('--board-card-bg', 'linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(255,255,255,0.98) 100%)');
    root.style.setProperty('--board-card-border', 'rgba(148,163,184,0.18)');
    root.style.setProperty('--board-card-shadow', '0 14px 42px rgba(15,23,42,0.08)');
    root.style.setProperty('--board-card-shadow-strong', '0 24px 60px rgba(15,23,42,0.10)');
    root.style.setProperty('--board-card-backdrop', 'blur(16px)');
    root.style.setProperty('--board-input-bg', 'rgba(255,255,255,0.98)');
    root.style.setProperty('--board-input-text', '#111827');
    root.style.setProperty('--board-input-border', 'rgba(148,163,184,0.28)');
    root.style.setProperty('--board-table-head-bg', 'rgba(248,250,252,0.92)');
    root.style.setProperty('--board-table-row-border', 'rgba(226,232,240,0.90)');
    root.style.setProperty('--board-chip-bg', 'rgba(15,23,42,0.04)');
    root.style.setProperty('--board-chip-border', 'rgba(148,163,184,0.18)');
    root.style.setProperty('--board-chip-text', '#334155');
    root.style.setProperty('--board-overlay', 'rgba(15,23,42,0.18)');
    root.style.setProperty('--board-selection-bg', hexToRgba(accent.base, 0.18));
    root.style.setProperty('--board-selection-text', '#0f172a');
    root.style.setProperty('--board-scroll-track', 'rgba(241,245,249,0.92)');
    root.style.setProperty('--board-scroll-thumb', 'rgba(148,163,184,0.36)');
    root.style.setProperty('--board-scroll-thumb-hover', 'rgba(100,116,139,0.46)');
    root.style.setProperty('--board-danger-bg', 'rgba(239,68,68,0.10)');
    root.style.setProperty('--board-danger-border', 'rgba(248,113,113,0.24)');
    root.style.setProperty('--board-danger-text', '#b91c1c');
    root.style.setProperty('--board-success-bg', 'rgba(34,197,94,0.10)');
    root.style.setProperty('--board-success-border', 'rgba(74,222,128,0.24)');
    root.style.setProperty('--board-success-text', '#166534');
    root.style.setProperty('--board-warning-bg', 'rgba(245,158,11,0.12)');
    root.style.setProperty('--board-warning-border', 'rgba(251,191,36,0.26)');
    root.style.setProperty('--board-warning-text', '#b45309');
    return;
  }

  const first = resolveHex(accent1, DEFAULT_ACCENT_1);
  const second = resolveHex(accent2, DEFAULT_ACCENT_2);
  const mixed = mixColors(first, second, 0.5);
  const buttonText = getReadableTextColor(mixed);

  root.style.setProperty('--brand-accent-1', first);
  root.style.setProperty('--brand-accent-2', second);
  root.style.setProperty('--brand-mix', mixed);
  root.style.setProperty('--board-accent-soft', hexToRgba(mixed, 0.16));
  root.style.setProperty('--board-accent-border', hexToRgba(mixed, 0.26));
  root.style.setProperty('--board-accent-gradient-soft', `linear-gradient(135deg, ${hexToRgba(first, 0.26)} 0%, ${hexToRgba(second, 0.26)} 100%)`);
  root.style.setProperty('--brand-button-bg', `linear-gradient(135deg, ${first} 0%, ${second} 100%)`);
  root.style.setProperty('--brand-button-text', buttonText);
  root.style.setProperty('--brand-button-border', hexToRgba(mixed, 0.42));
  root.style.setProperty('--brand-secondary-bg', 'rgba(255,255,255,0.06)');
  root.style.setProperty('--brand-secondary-text', '#f8fafc');
  root.style.setProperty('--brand-secondary-border', 'rgba(148,163,184,0.18)');

  root.style.setProperty('--board-page-text', '#f8fafc');
  root.style.setProperty('--board-text-strong', '#f8fafc');
  root.style.setProperty('--board-text-soft', 'rgba(241,245,249,0.88)');
  root.style.setProperty('--board-text-muted', 'rgba(226,232,240,0.62)');
  root.style.setProperty('--board-text-faint', 'rgba(226,232,240,0.48)');
  root.style.setProperty('--board-surface', 'rgba(10, 15, 28, 0.94)');
  root.style.setProperty('--board-surface-strong', 'rgba(7, 11, 22, 0.98)');
  root.style.setProperty('--board-surface-soft', 'rgba(255,255,255,0.04)');
  root.style.setProperty('--board-surface-soft-strong', 'rgba(255,255,255,0.06)');
  root.style.setProperty('--board-border', 'rgba(148,163,184,0.16)');
  root.style.setProperty('--board-border-strong', 'rgba(148,163,184,0.24)');
  root.style.setProperty('--board-planner-grid-line', 'rgba(148,163,184,0.10)');
  root.style.setProperty('--board-planner-grid-line-strong', 'rgba(148,163,184,0.16)');
  root.style.setProperty('--board-planner-track-sheen', 'rgba(255,255,255,0.02)');
  root.style.setProperty('--board-card-bg', 'linear-gradient(180deg, rgba(11, 15, 24, 0.96) 0%, rgba(9, 12, 20, 0.98) 100%)');
  root.style.setProperty('--board-card-border', 'rgba(148,163,184,0.10)');
  root.style.setProperty('--board-card-shadow', '0 10px 28px rgba(2,6,23,0.22)');
  root.style.setProperty('--board-card-shadow-strong', '0 24px 70px rgba(2,6,23,0.34)');
  root.style.setProperty('--board-card-backdrop', 'blur(14px)');
  root.style.setProperty('--board-input-bg', 'rgba(3, 8, 20, 0.72)');
  root.style.setProperty('--board-input-text', '#f8fafc');
  root.style.setProperty('--board-input-border', 'rgba(148,163,184,0.12)');
  root.style.setProperty('--board-table-head-bg', 'rgba(255,255,255,0.03)');
  root.style.setProperty('--board-table-row-border', 'rgba(148,163,184,0.10)');
  root.style.setProperty('--board-chip-bg', 'rgba(255,255,255,0.04)');
  root.style.setProperty('--board-chip-border', 'rgba(148,163,184,0.12)');
  root.style.setProperty('--board-chip-text', '#f8fafc');
  root.style.setProperty('--board-overlay', 'rgba(3, 6, 14, 0.72)');
  root.style.setProperty('--board-selection-bg', 'rgba(96, 165, 250, 0.28)');
  root.style.setProperty('--board-selection-text', '#f8fafc');
  root.style.setProperty('--board-scroll-track', 'rgba(15, 23, 42, 0.55)');
  root.style.setProperty('--board-scroll-thumb', 'rgba(148, 163, 184, 0.26)');
  root.style.setProperty('--board-scroll-thumb-hover', 'rgba(148, 163, 184, 0.38)');
  root.style.setProperty('--board-danger-bg', 'rgba(239,68,68,0.16)');
  root.style.setProperty('--board-danger-border', 'rgba(248,113,113,0.30)');
  root.style.setProperty('--board-danger-text', '#fee2e2');
  root.style.setProperty('--board-success-bg', 'rgba(34,197,94,0.16)');
  root.style.setProperty('--board-success-border', 'rgba(74,222,128,0.28)');
  root.style.setProperty('--board-success-text', '#dcfce7');
  root.style.setProperty('--board-warning-bg', 'rgba(245,158,11,0.18)');
  root.style.setProperty('--board-warning-border', 'rgba(251,191,36,0.28)');
  root.style.setProperty('--board-warning-text', '#fef3c7');
}

export const theme = {
  fontStack,
  page: {
    fontFamily: fontStack,
    minHeight: '100vh',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    color: 'var(--board-page-text, #f8fafc)',
    background:
      'radial-gradient(circle at 15% 18%, rgba(59,130,246,0.28) 0, transparent 30%), radial-gradient(circle at 82% 12%, rgba(99,102,241,0.22) 0, transparent 26%), radial-gradient(circle at 58% 82%, rgba(30,64,175,0.18) 0, transparent 34%), linear-gradient(180deg, #0a0f1d 0%, #060812 38%, #05070f 100%)',
    backgroundAttachment: 'fixed',
    overflowX: 'hidden',
  },
  shell: {
    width: '100%',
    padding: '18px',
    boxSizing: 'border-box',
  },
  glassCard: {
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    background: 'var(--ui-surface)',
    border: '1px solid var(--board-card-border, rgba(148,163,184,0.10))',
    borderRadius: '6px',
    boxShadow: 'none',
    backdropFilter: 'none',
  },
  table: {
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: 0,
  },
  th: {
    backgroundColor: 'var(--board-table-head-bg, rgba(255,255,255,0.03))',
    padding: '12px 14px',
    textAlign: 'left',
    borderBottom: '1px solid var(--board-border, rgba(148,163,184,0.16))',
    fontSize: '11px',
    color: 'var(--board-text-muted, rgba(226,232,240,0.56))',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    fontWeight: 700,
  },
  td: {
    padding: '12px 14px',
    borderBottom: '1px solid var(--board-table-row-border, rgba(148,163,184,0.10))',
    fontSize: '14px',
    color: 'var(--board-text-soft, rgba(241,245,249,0.88))',
    verticalAlign: 'top',
  },
  input: {
    padding: '10px 12px',
    border: '1px solid var(--board-input-border, rgba(148,163,184,0.12))',
    borderRadius: '6px',
    fontSize: '14px',
    background: 'var(--board-input-bg, rgba(3, 8, 20, 0.72))',
    color: 'var(--board-input-text, #f8fafc)',
    outline: 'none',
    boxSizing: 'border-box',
    boxShadow: 'none',
    colorScheme: 'var(--board-color-scheme, dark)',
  },
  select: {
    padding: '10px 12px',
    border: '1px solid var(--board-input-border, rgba(148,163,184,0.12))',
    borderRadius: '6px',
    fontSize: '14px',
    background: 'var(--board-input-bg, rgba(3, 8, 20, 0.78))',
    color: 'var(--board-input-text, #f8fafc)',
    outline: 'none',
    boxSizing: 'border-box',
    boxShadow: 'none',
    colorScheme: 'var(--board-color-scheme, dark)',
  },
  button: {
    padding: '9px 13px',
    background: 'var(--brand-secondary-bg, rgba(255,255,255,0.08))',
    color: 'var(--brand-secondary-text, #f8fafc)',
    border: '1px solid var(--brand-secondary-border, rgba(148,163,184,0.16))',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 700,
    boxShadow: 'none',
    transition: 'transform 120ms ease, border-color 120ms ease, background 120ms ease',
  },
  primaryButton: {
    background: 'var(--brand-button-bg, linear-gradient(135deg, #3b82f6 0%, #6366f1 100%))',
    color: 'var(--brand-button-text, #fff)',
    border: '1px solid var(--brand-button-border, rgba(99,102,241,0.34))',
  },
  dangerButton: {
    background: 'var(--board-danger-bg, rgba(239,68,68,0.16))',
    border: '1px solid var(--board-danger-border, rgba(248,113,113,0.30))',
    color: 'var(--board-danger-text, #fee2e2)',
  },
  successButton: {
    background: 'var(--board-success-bg, rgba(34,197,94,0.16))',
    border: '1px solid var(--board-success-border, rgba(74,222,128,0.28))',
    color: 'var(--board-success-text, #dcfce7)',
  },
  warningButton: {
    background: 'var(--board-warning-bg, rgba(245,158,11,0.18))',
    border: '1px solid var(--board-warning-border, rgba(251,191,36,0.28))',
    color: 'var(--board-warning-text, #fef3c7)',
  },
  mutedButton: {
    background: 'var(--brand-secondary-bg, rgba(255,255,255,0.05))',
    border: '1px solid var(--brand-secondary-border, rgba(148,163,184,0.16))',
    color: 'var(--brand-secondary-text, #e2e8f0)',
  },
  timelineSurface: {
    background: 'var(--board-card-bg, linear-gradient(180deg, rgba(10,15,27,0.98) 0%, rgba(7,10,20,0.98) 100%))',
    border: '1px solid var(--board-card-border, rgba(148,163,184,0.10))',
    borderRadius: '6px',
    boxShadow: 'none',
    backdropFilter: 'var(--board-card-backdrop, blur(12px))',
  },
  pageHeading: {
    display: 'grid',
    gap: '10px',
  },
  pageEyebrow: {
    margin: 0,
    fontSize: '11px',
    fontWeight: 800,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    color: 'var(--board-text-faint, rgba(226,232,240,0.52))',
  },
  pageTitle: {
    margin: 0,
    fontSize: 'clamp(28px, 3vw, 42px)',
    fontWeight: 800,
    letterSpacing: '-0.03em',
    color: 'var(--board-text-strong, #f8fafc)',
  },
  pageSubtitle: {
    margin: 0,
    maxWidth: '720px',
    fontSize: '14px',
    lineHeight: 1.6,
    color: 'var(--board-text-muted, rgba(226,232,240,0.68))',
  },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 10px',
    borderRadius: '999px',
    background: 'var(--board-chip-bg, rgba(255,255,255,0.04))',
    border: '1px solid var(--board-chip-border, rgba(148,163,184,0.12))',
    color: 'var(--board-chip-text, #f8fafc)',
    fontSize: '11px',
    fontWeight: 600,
  },
  subtleText: {
    color: 'var(--board-text-muted, rgba(226,232,240,0.62))',
  },
};

export function mergeStyles(...styles) {
  return Object.assign({}, ...styles);
}
